/**
 * @typedef {object} User
 * @property {number} id - the id of the user
 * @property {string} name - The name of the user.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  PaginationItem,
} from '@mui/material';

import useSortable from '../../hooks/useSortable';
import UsersListItem from './UsersListItem';
import FetchError from '../UI/fetch-error/FetchError';
import Loader from '../UI/loader/Loader';
import { TableHeadCell } from '../table-cells/TableCells';
import Tooltip from '../tooltip/Tooltip';

import { ReactComponent as InfoIcon } from '../../icons/info.svg';
import { PreviousPageIcon, NextPageIcon } from '../../PaginationIcons';

import styles from './Users.module.scss';

import {
  useGetAuthenticationQuery,
  useGetUsersWithProfilesQuery,
  useGetCollectionsQuery,
} from '../../services/ADapi';

const UsersList = ({ searchQuery, assignedFilter }) => {
  const [page, setPage] = useState(1);

  const {
    data: { user_id },
  } = useGetAuthenticationQuery();

  const { data: collections = [] } = useGetCollectionsQuery(user_id);

  const {
    data: allUsers = [],
    isLoading,
    isError,
  } = useGetUsersWithProfilesQuery(user_id);

  const usersAssignedToCollections = useMemo(() => {
    return collections.reduce((users, collection) => {
      users.push(...collection.ad_users);

      return users;
    }, []);
  }, [collections]);

  const filteredUsers = useMemo(() => {
    if (allUsers.length === 0) {
      return allUsers;
    }

    return allUsers.filter((user) => {
      if (assignedFilter === 'assigned') {
        return usersAssignedToCollections.includes(user.id);
      }

      if (assignedFilter === 'unassigned') {
        return !usersAssignedToCollections.includes(user.id);
      }

      if (!searchQuery) {
        return true;
      }

      const query = searchQuery.toLowerCase();

      return user.name.toLowerCase().includes(query);
    });
  }, [allUsers, assignedFilter, searchQuery, usersAssignedToCollections]);

  let { result, requestSort } = useSortable(filteredUsers);

  const ITEMS_PER_PAGE = 5;
  const paginatedDomains = result.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  useEffect(() => {
    if (searchQuery.length > 0) {
      setPage(1);
    }
  }, [searchQuery]);

  const lastSeenHandler = () => {
    requestSort('last_seen');
  };

  const pageHandler = (_e, value) => {
    setPage(value);
  };

  if (isLoading) {
    return <Loader text="Please wait..." />;
  }

  if (isError) {
    return <FetchError />;
  }

  if (allUsers.length === 0) {
    return <Typography variant="body1">No Users yet</Typography>;
  }

  return (
    <React.Fragment>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="users list">
          <TableHead>
            <TableRow>
              <TableHeadCell>Users</TableHeadCell>
              <TableHeadCell align="left">Collections</TableHeadCell>
              <TableHeadCell align="left">
                Policy
                <Tooltip
                  arrow
                  classes={{
                    tooltip: styles['MuiTooltip-tooltip'],
                    arrow: styles['MuiTooltip-arrow'],
                  }}
                  title="The active policy specified is from the collection with the highest priority. You can change the priority of a collection in the Collections tab"
                >
                  <InfoIcon />
                </Tooltip>
              </TableHeadCell>
              <TableHeadCell align="left">PC</TableHeadCell>
              <TableHeadCell align="left">OS</TableHeadCell>
              <TableHeadCell align="left" onClick={lastSeenHandler}>
                Last seen
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDomains.map(
              ({ id, name, profile_id, os_info, hostname, last_seen }) => (
                <UsersListItem
                  key={id}
                  id={id}
                  user_name={name}
                  collections={collections.map((collection, index) => {
                    if (collection.ad_users.indexOf(id) >= 0) {
                      return <p key={index}>{collection.name}</p>;
                    }

                    return null;
                  })}
                  profileId={profile_id}
                  os_info={os_info}
                  hostname={hostname}
                  last_seen={last_seen}
                />
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {allUsers.length > ITEMS_PER_PAGE ? (
        <Grid container sx={{ justifyContent: 'flex-end', pt: 5 }}>
          <Pagination
            count={Math.ceil(allUsers.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={pageHandler}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                slots={{
                  next: NextPageIcon,
                  previous: PreviousPageIcon,
                }}
              />
            )}
          />
        </Grid>
      ) : null}
    </React.Fragment>
  );
};

export default UsersList;
